import { useLitterFormState } from '@application/state';
import type { MtLitterGetLitterQuery } from '@gql/graphql';
import { ParentTypeEnum } from '@gql/graphql';
import type { MtLitterPageFormValues } from '@presentation/pages';
import dayjs from 'dayjs';
import { useCallback } from 'react';

export const DEFAULT_LITTER_INITAL_VALUES: MtLitterPageFormValues = {
  fixedPrice: true,
  pedigree: undefined,
  hasLitterCode: false,
  litterCode: null,
  malesCount: 0,
  femalesCount: 0,
  availabilityDate: undefined,
  birthday: undefined,
  born: undefined,
  breedId: undefined,
  description: undefined,
  dewormed: undefined,
  images: [undefined],
  microchipped: undefined,
  price: undefined,
  priceFrom: undefined,
  priceTo: undefined,
  vaccinated: undefined,
  vaccinationDate: undefined,
  FATHER: undefined,
  fatherPedigree: undefined,
  MOTHER: undefined,
  motherPedigree: undefined,
  malesLeftCount: 0,
  femalesLeftCount: 0,
};

export const useLitterInitialValuesBuilder = () => {
  const { litterFormState } = useLitterFormState();

  const litterInitialValuesBuilder = useCallback(
    (
      litter?: NonNullable<
        NonNullable<MtLitterGetLitterQuery['accountMe']>['dogBreeder']
      >['litters'][number],
    ): MtLitterPageFormValues => {
      if (litterFormState.values && (litter?.id ?? 'new') === litterFormState.litterId)
        return litterFormState.values;
      if (!litter) return DEFAULT_LITTER_INITAL_VALUES;

      return {
        // General
        images: (litter.imgs ?? []).filter((img) => !!img.small).map((img) => img.small as string),
        breedId: litter.dogBreed?.id ?? DEFAULT_LITTER_INITAL_VALUES.breedId,
        born: litter.born,
        price: litter.price ?? DEFAULT_LITTER_INITAL_VALUES.price,
        priceFrom: litter.priceFrom ?? DEFAULT_LITTER_INITAL_VALUES.priceFrom,
        priceTo: litter.priceTo ?? DEFAULT_LITTER_INITAL_VALUES.priceTo,
        fixedPrice: litter.fixedPrice ?? DEFAULT_LITTER_INITAL_VALUES.fixedPrice,
        birthday: litter.birthday ? dayjs(litter.birthday) : DEFAULT_LITTER_INITAL_VALUES.birthday,
        availabilityDate: litter.availabilityDate
          ? dayjs(litter.availabilityDate)
          : DEFAULT_LITTER_INITAL_VALUES.availabilityDate,
        description: litter.description ?? DEFAULT_LITTER_INITAL_VALUES.description,
        // Parents
        FATHER:
          litter.parents.find((x) => x.parentType === ParentTypeEnum.FATHER)?.parentId ?? undefined,
        fatherPedigree:
          litter.parents.find((x) => x.parentType === ParentTypeEnum.FATHER)?.parent?.dogInfos
            ?.pedigreeNumber ?? undefined,
        MOTHER:
          litter.parents.find((x) => x.parentType === ParentTypeEnum.MOTHER)?.parentId ?? undefined,
        motherPedigree:
          litter.parents.find((x) => x.parentType === ParentTypeEnum.MOTHER)?.parent?.dogInfos
            ?.pedigreeNumber ?? undefined,
        // Litters
        malesCount: litter.malesCount ?? DEFAULT_LITTER_INITAL_VALUES.malesCount,
        femalesCount: litter.femalesCount ?? DEFAULT_LITTER_INITAL_VALUES.femalesCount,
        femalesLeftCount: litter.femalesLeftCount ?? DEFAULT_LITTER_INITAL_VALUES.femalesLeftCount,
        malesLeftCount: litter.malesLeftCount ?? DEFAULT_LITTER_INITAL_VALUES.malesLeftCount,
        microchipped: litter.microchipped ?? DEFAULT_LITTER_INITAL_VALUES.microchipped,
        vaccinated: litter.vaccinated ?? DEFAULT_LITTER_INITAL_VALUES.vaccinated,
        vaccinationDate: litter.vaccinationDate
          ? dayjs(litter.vaccinationDate)
          : DEFAULT_LITTER_INITAL_VALUES.vaccinationDate,
        dewormed: litter.dewormed ?? DEFAULT_LITTER_INITAL_VALUES.dewormed,
        pedigree:
          litter.pedigree !== undefined ? litter.pedigree : DEFAULT_LITTER_INITAL_VALUES.pedigree,
        hasLitterCode: !!litter.litterCode,
        litterCode: litter.litterCode ?? DEFAULT_LITTER_INITAL_VALUES.litterCode,
      };
    },
    [litterFormState],
  );

  return { litterInitialValuesBuilder };
};
